import { IconChevronLeft } from "central-icons/IconChevronLeft";
import { IconInboxEmpty } from "central-icons/IconInboxEmpty";
import { type FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IconWorkflows } from "@/assets/icons/central/IconAutomations";
import { IconCheckFill } from "@/assets/icons/geist/IconCheckFill";
import { IconChevronDown } from "@/assets/icons/geist/IconChevronDown";
import { IconEllipsisHorizontal } from "@/assets/icons/geist/IconEllipsisHorizontal";
import { IconEye } from "@/assets/icons/geist/IconEye";
import { IconEyeOff } from "@/assets/icons/geist/IconEyeOff";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { Notification as CountBadge } from "@/components/flexkit/badge/Notification";
import { Button } from "@/components/flexkit/Button";
import {
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuPortal,
    ContextMenuRoot,
    ContextMenuTrigger,
} from "@/components/flexkit/ContextMenu";
import { StatusDot } from "@/components/flexkit/status-dot/StatusDot";
import { useToast } from "@/components/flexkit/toast";
import { Heading2 } from "@/components/flexkit/typography/Headings";
import {
    groupByTimePeriod,
    groupNotifications,
    type NotificationGroup,
} from "@/components/notifications/group-notifications";
import { DropdownMenuItem } from "@/components/podkit/dropdown/DropDown";
import { DropdownActions } from "@/components/podkit/dropdown/DropDownActions";
import { cn } from "@/components/podkit/lib/cn";
import { timeAgo } from "@/format/time";
import { useExitTransition } from "@/hooks/use-exit-transition";
import {
    useDeleteNotifications,
    useMarkNotificationsRead,
    useMarkNotificationsUnread,
    useNotificationList,
} from "@/hooks/use-notifications";
import { useSegmentTrack } from "@/hooks/use-segment";
import type { EnvironmentDetailsNavState } from "@/routes/environments/details/environment-nav-state";
import { timestampDate } from "@/utils/timestamp";
import type { Notification } from "gitpod-next-api/gitpod/v1/notification_pb";
import { NotificationType } from "gitpod-next-api/gitpod/v1/notification_pb";

type NotificationInboxProps = {
    onClose: () => void;
    /** When true, show the close button in the header. Defaults to true. */
    showClose?: boolean;
    /** Called after a notification item navigates. Used on mobile to close the panel. */
    onNavigate?: () => void;
};

export const NotificationInbox: FC<NotificationInboxProps> = ({ onClose, showClose = true, onNavigate }) => {
    const { data: notifications, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useNotificationList();
    const { mutateAsync: markReadAsync } = useMarkNotificationsRead();
    const { mutateAsync: markUnreadAsync } = useMarkNotificationsUnread();
    const { mutateAsync: deleteNotificationAsync } = useDeleteNotifications();
    const sentinelRef = useRef<HTMLDivElement>(null);

    // Operates on loaded pages only — notifications beyond the current scroll
    // position are not affected. The API requires explicit IDs (max 25 per call)
    // and has no bulk-all endpoint.
    const handleMarkAllRead = useCallback(async () => {
        if (!notifications?.length) return;
        const unreadIds = notifications.filter((n) => !n.readAt).map((n) => n.id);
        if (unreadIds.length === 0) return;
        await markReadAsync(unreadIds);
    }, [notifications, markReadAsync]);

    const [menuOpen, setMenuOpen] = useState(false);

    const handleDeleteAll = useCallback(async () => {
        if (!notifications?.length) return;
        setMenuOpen(false);
        await deleteNotificationAsync(notifications.map((n) => n.id));
    }, [notifications, deleteNotificationAsync]);

    const wrappedMarkAllRead = useCallback(() => {
        setMenuOpen(false);
        void handleMarkAllRead();
    }, [handleMarkAllRead]);

    const sections = useMemo(() => {
        const groups = groupNotifications(notifications ?? []);
        return groupByTimePeriod(groups);
    }, [notifications]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
                    void fetchNextPage();
                }
            },
            { threshold: 0 },
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    return (
        <div className="flex h-full flex-col bg-surface-01">
            <div className="flex h-12 items-center gap-2 border-b border-border-subtle px-4 py-2">
                <Heading2 className="truncate font-semibold">Activity</Heading2>
                <div className="ml-auto flex items-center gap-1">
                    <DropdownActions
                        triggerTestId="notification-actions-menu"
                        align="end"
                        contentClassName="w-40"
                        open={menuOpen}
                        onOpenChange={setMenuOpen}
                    >
                        <DropdownMenuItem
                            onClick={wrappedMarkAllRead}
                            data-tracking-id="mark-all-read"
                            LeadingIcon={IconCheckFill}
                        >
                            Mark all read
                        </DropdownMenuItem>
                        <DropdownMenuItem
                            onClick={handleDeleteAll}
                            data-tracking-id="delete-all"
                            LeadingIcon={IconTrash}
                        >
                            Delete all
                        </DropdownMenuItem>
                    </DropdownActions>
                    {showClose && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={onClose}
                            aria-label="Close activity"
                            data-tracking-id="close-activity"
                            tabIndex={-1}
                        >
                            <IconChevronLeft size={16} className="text-content-secondary" />
                        </Button>
                    )}
                </div>
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 py-2">
                {isLoading && (
                    <div className="flex items-center justify-center py-8">
                        <span className="text-sm text-content-tertiary">Loading...</span>
                    </div>
                )}

                {!isLoading && (!notifications || notifications.length === 0) && (
                    <div className="flex flex-1 flex-col items-center justify-center gap-2">
                        <IconInboxEmpty size={24} className="text-content-tertiary" />
                        <span className="text-sm text-content-tertiary">You&apos;re all caught up</span>
                    </div>
                )}

                {sections.map((section) => (
                    <div key={section.key} data-testid="notification-section" className="flex flex-col gap-2">
                        <div
                            className="sticky top-0 z-10 flex h-8 items-center justify-between bg-surface-01"
                            data-testid="notification-section-header"
                        >
                            <span className="text-sm font-medium text-content-secondary">{section.label}</span>
                            <CountBadge
                                content={String(section.unreadCount)}
                                className={cn(section.unreadCount === 0 && "invisible")}
                            />
                        </div>
                        {section.groups.map((group) => (
                            <NotificationGroupItem
                                key={group.key}
                                group={group}
                                onMarkRead={markReadAsync}
                                onMarkUnread={markUnreadAsync}
                                onDelete={deleteNotificationAsync}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </div>
                ))}

                <div ref={sentinelRef} className="h-1" />

                {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-4">
                        <span className="text-sm text-content-tertiary">Loading more...</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const notificationRowClass = "group cursor-pointer select-none rounded-lg p-1 pr-2 hover:bg-surface-hover";
const notificationMetaClass = "flex items-center gap-1 truncate text-sm text-content-muted";

type NotificationGroupItemProps = {
    group: NotificationGroup;
    onMarkRead: (ids: string[]) => Promise<unknown>;
    onMarkUnread: (ids: string[]) => Promise<unknown>;
    onDelete: (ids: string[]) => Promise<unknown>;
    onNavigate?: () => void;
};

const NotificationGroupItem: FC<NotificationGroupItemProps> = ({
    group,
    onMarkRead,
    onMarkUnread,
    onDelete,
    onNavigate,
}) => {
    const segmentTrack = useSegmentTrack();
    const [expanded, setExpanded] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const { shouldRender: isActedUpon } = useExitTransition(dropdownOpen || contextMenuOpen, { duration: 150 });
    const navigate = useNavigate();
    const { toast } = useToast();

    const latest = group.latest;

    const wrappedOnNavigate = useCallback(() => {
        segmentTrack("notification_navigated", { type: notificationTypeTrackingValue(latest.type) });
        onNavigate?.();
    }, [segmentTrack, latest.type, onNavigate]);

    if (group.items.length === 1) {
        return (
            <div className="rounded-xl border border-border-base">
                <NotificationItem
                    notification={group.items[0]}
                    onMarkRead={onMarkRead}
                    onMarkUnread={onMarkUnread}
                    onDelete={onDelete}
                    onNavigate={onNavigate}
                />
            </div>
        );
    }

    const createdAt = timestampDate(latest.createdAt) ?? new Date();
    const unreadIds = group.items.filter((n) => !n.readAt).map((n) => n.id);
    const readIds = group.items.filter((n) => n.readAt).map((n) => n.id);
    const allIds = group.items.map((n) => n.id);

    const handleActivate = () => {
        segmentTrack("notification_clicked", {
            type: notificationTypeTrackingValue(latest.type),
            age_seconds: Math.floor((Date.now() - createdAt.getTime()) / 1000),
        });
        if (unreadIds.length > 0) {
            void onMarkRead(unreadIds);
        }
        navigateToNotification(latest, navigate, toast, wrappedOnNavigate);
    };

    const handleToggleRead = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (group.hasUnread) {
            void onMarkRead(unreadIds);
        } else {
            void onMarkUnread(readIds);
        }
    };

    const handleDeleteAll = (e: React.MouseEvent) => {
        e.stopPropagation();
        void onDelete(allIds);
    };

    return (
        <div data-testid="notification-group" className="rounded-xl border border-border-base">
            <ContextMenuRoot onOpenChange={setContextMenuOpen}>
                <ContextMenuTrigger asChild>
                    <div
                        role="button"
                        tabIndex={0}
                        className={cn(notificationRowClass, isActedUpon && "bg-surface-hover")}
                        onClick={handleActivate}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleActivate();
                            }
                        }}
                        data-tracking-id="notification-group-header"
                        data-testid="notification-group-header"
                    >
                        <div className="flex items-start gap-2">
                            {AGENT_EXECUTION_TYPES.has(group.type) && (
                                <OnaLettermark className="size-4 shrink-0 self-start text-content-tertiary" />
                            )}
                            {AUTOMATION_TYPES.has(group.type) && (
                                <span className="size-4 shrink-0 self-start text-content-tertiary">
                                    <IconWorkflows />
                                </span>
                            )}
                            <div className="min-w-0 flex-1">
                                <span className={notificationMetaClass}>
                                    {notificationTypeLabel(group.type, "group")}
                                    <StatusDot
                                        size="sm"
                                        color="brand"
                                        variant="solid"
                                        className={cn(!group.hasUnread && "invisible")}
                                    />
                                </span>

                                <p
                                    className={cn(
                                        "truncate text-base text-content-primary",
                                        group.hasUnread ? "font-semibold" : "font-medium",
                                    )}
                                >
                                    {latest.title}
                                </p>

                                {latest.body && (
                                    <p className="mt-0.5 line-clamp-2 text-xs text-content-tertiary">{latest.body}</p>
                                )}

                                {!expanded && (
                                    // eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- expand action, tracked via show-all
                                    <div
                                        className="relative mt-1.5 flex items-center pl-6"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setExpanded(true);
                                        }}
                                    >
                                        <div className="absolute -top-0.5 left-0 h-2.5 w-5 rounded-bl-lg border-b border-l border-border-strong" />
                                        <span className="rounded-md px-1 py-0.5 text-xs font-semibold text-content-muted hover:bg-surface-hover">
                                            {`${group.items.length} ${group.items.length === 1 ? "activity" : "activities"}`}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- stopPropagation only, not a user action */}
                            <div className="shrink-0 self-start" onClick={(e) => e.stopPropagation()}>
                                {expanded ? (
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        className="h-6 w-6 p-0"
                                        onClick={() => setExpanded(false)}
                                        aria-label="Collapse group"
                                        data-testid="notification-group-toggle"
                                        data-tracking-id="collapse-group"
                                    >
                                        <IconChevronDown
                                            size="sm"
                                            className="rotate-180 text-content-secondary transition-transform duration-150 ease-out"
                                        />
                                    </Button>
                                ) : (
                                    <div className="flex flex-col items-end gap-1">
                                        <span className="text-sm text-content-muted">{timeAgo(createdAt)}</span>
                                        <div className="flex items-center">
                                            <DropdownActions
                                                triggerTestId="notification-group-actions"
                                                align="end"
                                                contentClassName="w-40"
                                                open={dropdownOpen}
                                                onOpenChange={setDropdownOpen}
                                                button={
                                                    <Button
                                                        LeadingIcon={IconEllipsisHorizontal}
                                                        size="xs"
                                                        variant="ghost"
                                                        aria-label="More actions"
                                                        className="border-0 text-content-secondary"
                                                        data-tracking-id="notification-group-actions"
                                                    />
                                                }
                                            >
                                                <DropdownMenuItem
                                                    onClick={() => setExpanded(true)}
                                                    data-testid="notification-group-toggle"
                                                    data-tracking-id="show-all"
                                                    LeadingIcon={IconEye}
                                                >
                                                    Show all
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={handleToggleRead}
                                                    data-tracking-id={
                                                        group.hasUnread ? "mark-group-read" : "mark-group-unread"
                                                    }
                                                    LeadingIcon={IconCheckFill}
                                                >
                                                    {group.hasUnread ? "Mark as read" : "Mark as unread"}
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={handleDeleteAll}
                                                    data-tracking-id="delete-group"
                                                    LeadingIcon={IconTrash}
                                                >
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownActions>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ContextMenuTrigger>
                <ContextMenuPortal>
                    <ContextMenuContent>
                        <ContextMenuItem
                            onClick={() => setExpanded(!expanded)}
                            data-tracking-id={expanded ? "hide-all-context" : "show-all-context"}
                            LeadingIcon={expanded ? IconEyeOff : IconEye}
                        >
                            {expanded ? "Hide all" : "Show all"}
                        </ContextMenuItem>
                        <ContextMenuItem
                            onClick={handleToggleRead}
                            data-tracking-id={group.hasUnread ? "mark-group-read-context" : "mark-group-unread-context"}
                            LeadingIcon={IconCheckFill}
                        >
                            {group.hasUnread ? "Mark as read" : "Mark as unread"}
                        </ContextMenuItem>
                        <ContextMenuItem
                            onClick={handleDeleteAll}
                            data-tracking-id="delete-group-context"
                            LeadingIcon={IconTrash}
                        >
                            Delete
                        </ContextMenuItem>
                    </ContextMenuContent>
                </ContextMenuPortal>
            </ContextMenuRoot>

            {expanded && (
                <div
                    className={cn(
                        "pl-1",
                        (AGENT_EXECUTION_TYPES.has(group.type) || AUTOMATION_TYPES.has(group.type)) && "pl-7",
                    )}
                    data-testid="notification-group-children"
                >
                    {group.items.map((n, i) => {
                        const isLast = i === group.items.length - 1;
                        return (
                            <div key={n.id} className="flex items-stretch">
                                {/* Connector column — every child gets a curved elbow.
                                   Non-last also gets a trunk segment below the elbow. */}
                                <div className="relative w-5 shrink-0">
                                    {/* Curved elbow — border-left from top to curve point,
                                       border-bottom curves into the content */}
                                    <div className="h-3.5 w-full rounded-bl-lg border-b border-l border-border-strong" />
                                    {/* Trunk continuation below the elbow — connects to next sibling */}
                                    {!isLast && (
                                        <div className="absolute bottom-0 left-0 top-3.5 border-l border-border-strong" />
                                    )}
                                </div>
                                {/* Content */}
                                <div className="min-w-0 flex-1">
                                    <NotificationItem
                                        notification={n}
                                        onMarkRead={onMarkRead}
                                        onMarkUnread={onMarkUnread}
                                        onDelete={onDelete}
                                        onNavigate={onNavigate}
                                        labelVariant="child"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export type NotificationItemProps = {
    notification: Notification;
    onMarkRead: (ids: string[]) => Promise<unknown>;
    onMarkUnread: (ids: string[]) => Promise<unknown>;
    onDelete: (ids: string[]) => Promise<unknown>;
    onNavigate?: () => void;
    /** Controls the type label text. Defaults to "standalone". */
    labelVariant?: LabelVariant;
};

const AGENT_EXECUTION_TYPES = new Set([NotificationType.AGENT_COMPLETED, NotificationType.AGENT_WAITING_FOR_INPUT]);
const AUTOMATION_TYPES = new Set([NotificationType.AUTOMATION_COMPLETED, NotificationType.AUTOMATION_STOPPED]);

export const NotificationItem: FC<NotificationItemProps> = ({
    notification,
    onMarkRead,
    onMarkUnread,
    onDelete,
    onNavigate,
    labelVariant = "standalone",
}) => {
    const segmentTrack = useSegmentTrack();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [contextMenuOpen, setContextMenuOpen] = useState(false);
    const isUnread = !notification.readAt;
    const createdAt = timestampDate(notification.createdAt) ?? new Date();

    const wrappedOnNavigate = useCallback(() => {
        segmentTrack("notification_navigated", { type: notificationTypeTrackingValue(notification.type) });
        onNavigate?.();
    }, [segmentTrack, notification.type, onNavigate]);

    const handleActivate = useCallback(() => {
        const age = timestampDate(notification.createdAt);
        segmentTrack("notification_clicked", {
            type: notificationTypeTrackingValue(notification.type),
            age_seconds: age ? Math.floor((Date.now() - age.getTime()) / 1000) : 0,
        });
        if (isUnread) {
            void onMarkRead([notification.id]);
        }
        navigateToNotification(notification, navigate, toast, wrappedOnNavigate);
    }, [isUnread, onMarkRead, notification, navigate, toast, wrappedOnNavigate, segmentTrack]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleActivate();
            }
        },
        [handleActivate],
    );

    const { shouldRender: isActedUpon } = useExitTransition(dropdownOpen || contextMenuOpen, { duration: 150 });

    return (
        <ContextMenuRoot onOpenChange={setContextMenuOpen}>
            <ContextMenuTrigger asChild>
                <div
                    role="button"
                    tabIndex={0}
                    className={cn(notificationRowClass, isActedUpon && "bg-surface-hover")}
                    onClick={handleActivate}
                    onKeyDown={handleKeyDown}
                    data-tracking-id="notification-item"
                    data-testid="notification-item"
                    data-unread={isUnread || undefined}
                >
                    <div className="flex items-start gap-2">
                        {labelVariant !== "child" && AGENT_EXECUTION_TYPES.has(notification.type) && (
                            <OnaLettermark className="size-4 shrink-0 self-start text-content-tertiary" />
                        )}
                        {labelVariant !== "child" && AUTOMATION_TYPES.has(notification.type) && (
                            <span className="size-4 shrink-0 self-start text-content-tertiary">
                                <IconWorkflows />
                            </span>
                        )}
                        <div className="min-w-0 flex-1">
                            <span className={notificationMetaClass}>
                                {notificationTypeLabel(notification.type, labelVariant)}
                                <StatusDot
                                    size="sm"
                                    color="brand"
                                    variant="solid"
                                    className={cn(!isUnread && "invisible")}
                                />
                            </span>

                            {labelVariant !== "child" && (
                                <p
                                    className={cn(
                                        "truncate text-base text-content-primary",
                                        isUnread ? "font-semibold" : "font-medium",
                                    )}
                                >
                                    {notification.title}
                                </p>
                            )}

                            {notification.body && (
                                <p className="mt-0.5 line-clamp-2 text-xs text-content-tertiary">{notification.body}</p>
                            )}
                        </div>

                        {/* eslint-disable-next-line gitpod-dashboard/require-onclick-tracking-id -- stopPropagation only, not a user action */}
                        <div
                            className="flex shrink-0 flex-col items-end gap-1 self-start"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <span className="text-sm text-content-muted">{timeAgo(createdAt)}</span>
                            <div className="flex items-center">
                                <DropdownActions
                                    triggerTestId="notification-item-actions"
                                    align="end"
                                    contentClassName="w-40"
                                    open={dropdownOpen}
                                    onOpenChange={setDropdownOpen}
                                    button={
                                        <Button
                                            LeadingIcon={IconEllipsisHorizontal}
                                            size="xs"
                                            variant="ghost"
                                            aria-label="More actions"
                                            className="border-0 text-content-secondary"
                                            data-tracking-id="notification-item-actions"
                                        />
                                    }
                                >
                                    <DropdownMenuItem
                                        onClick={() =>
                                            void (isUnread
                                                ? onMarkRead([notification.id])
                                                : onMarkUnread([notification.id]))
                                        }
                                        data-tracking-id={isUnread ? "mark-read" : "mark-unread"}
                                        LeadingIcon={IconCheckFill}
                                    >
                                        {isUnread ? "Mark as read" : "Mark as unread"}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        onClick={() => void onDelete([notification.id])}
                                        data-tracking-id="delete-activity"
                                        LeadingIcon={IconTrash}
                                    >
                                        Delete
                                    </DropdownMenuItem>
                                </DropdownActions>
                            </div>
                        </div>
                    </div>
                </div>
            </ContextMenuTrigger>
            <ContextMenuPortal>
                <ContextMenuContent>
                    <ContextMenuItem
                        onClick={() =>
                            void (isUnread ? onMarkRead([notification.id]) : onMarkUnread([notification.id]))
                        }
                        data-tracking-id={isUnread ? "mark-read-context" : "mark-unread-context"}
                        LeadingIcon={IconCheckFill}
                    >
                        {isUnread ? "Mark as read" : "Mark as unread"}
                    </ContextMenuItem>
                    <ContextMenuItem
                        onClick={() => void onDelete([notification.id])}
                        data-tracking-id="delete-activity-context"
                        LeadingIcon={IconTrash}
                    >
                        Delete
                    </ContextMenuItem>
                </ContextMenuContent>
            </ContextMenuPortal>
        </ContextMenuRoot>
    );
};

function navigateToNotification(
    notification: Notification,
    navigate: ReturnType<typeof useNavigate>,
    toast: ReturnType<typeof useToast>["toast"],
    onNavigate?: () => void,
) {
    const ctx = notification.context?.context;

    if (AGENT_EXECUTION_TYPES.has(notification.type)) {
        if (ctx?.case !== "agentExecution" || !ctx.value.environmentId || !ctx.value.agentExecutionId) {
            toast({ title: "Cannot navigate", description: "This activity item is missing details." });
            return;
        }
        const state: EnvironmentDetailsNavState = {
            preferredAgentExecutionId: ctx.value.agentExecutionId,
        };
        navigate(`/details/${ctx.value.environmentId}`, { state });
        onNavigate?.();
        return;
    }

    if (AUTOMATION_TYPES.has(notification.type)) {
        if (ctx?.case !== "workflowExecution" || !ctx.value.workflowExecutionId) {
            toast({ title: "Cannot navigate", description: "This activity item is missing details." });
            return;
        }
        navigate(`/automations/executions/${ctx.value.workflowExecutionId}`);
        onNavigate?.();
        return;
    }
}

/** Derives a snake_case tracking string from the NotificationType enum name. */
function notificationTypeTrackingValue(type: NotificationType): string {
    const name = NotificationType[type];
    if (!name || name === "UNSPECIFIED") return "unknown";
    return name.toLowerCase();
}

type LabelVariant = "standalone" | "group" | "child";

function notificationTypeLabel(type: NotificationType, variant: LabelVariant = "standalone"): string {
    switch (type) {
        case NotificationType.AGENT_COMPLETED:
            switch (variant) {
                case "group":
                    return "Agent";
                case "child":
                    return "Completed task";
                default:
                    return "Agent Completed";
            }
        case NotificationType.AGENT_WAITING_FOR_INPUT:
            switch (variant) {
                case "group":
                    return "Agent";
                case "child":
                    return "Waiting for Input";
                default:
                    return "Agent Waiting";
            }
        case NotificationType.ENVIRONMENT_ARCHIVED:
            return "Environment Archived";
        case NotificationType.ENVIRONMENT_LIFETIME_EXCEEDED:
            return "Environment Lifetime Exceeded";
        case NotificationType.USER_ADDED_TO_GROUP:
            return "Added to Group";
        case NotificationType.USER_REMOVED_FROM_GROUP:
            return "Removed from Group";
        case NotificationType.PROJECT_SHARED_WITH_GROUP:
            return "Project Shared with Group";
        case NotificationType.PROJECT_REMOVED_FROM_GROUP:
            return "Project Removed from Group";
        case NotificationType.USER_ADDED_TO_TEAM:
            return "Added to Team";
        case NotificationType.USER_REMOVED_FROM_TEAM:
            return "Removed from Team";
        case NotificationType.BILLING_CREDITS_LOW:
            return "Billing Credits Low";
        case NotificationType.BILLING_PAYMENT_FAILED:
            return "Billing Payment Failed";
        case NotificationType.BILLING_ACCOUNT_SUSPENDED:
            return "Billing Account Suspended";
        case NotificationType.AUTOMATION_COMPLETED:
            return "Automation Completed";
        case NotificationType.AUTOMATION_STOPPED:
            return "Automation Stopped";
        case NotificationType.PREBUILD_FAILED:
            return "Prebuild Failed";
        default:
            return "Activity";
    }
}
